import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Drawer as MuiDrawer,
  IconButton,
  type DrawerProps as MuiDrawerProps,
} from '@mui/material'
import type { ReactNode } from 'react'

export type DrawerProps = Omit<MuiDrawerProps, 'children'> & {
  children: ReactNode
  closable?: boolean
  header?: ReactNode
  height?: number | string
  hideHeader?: boolean
  maxHeight?: number | string
  overlay?: boolean
  width?: number | string
}

export function Drawer({
  anchor = 'right',
  children,
  closable = true,
  header,
  height,
  hideHeader = false,
  maxHeight,
  onClose,
  open = false,
  overlay = true,
  slotProps,
  width,
  ...props
}: DrawerProps) {
  const sizeStyles =
    anchor === 'left' || anchor === 'right'
      ? {
          maxWidth: '100%',
          width: width ?? 360,
        }
      : {
          height: height ?? 260,
          maxHeight: maxHeight ?? '100%',
        }

  const closeButton = closable ? (
    <IconButton aria-label="close drawer" onClick={event => onClose?.(event, 'escapeKeyDown')}>
      <CloseIcon />
    </IconButton>
  ) : null

  const showHeaderRow = !hideHeader && (header || closable)
  const showFloatingCloseButton = hideHeader && closable

  const content = (
    <>
      {showHeaderRow ? (
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: header ? '1px solid' : 'none',
            borderColor: 'divider',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'space-between',
            minHeight: 56,
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ flex: 1, fontSize: '1rem', fontWeight: 600, pr: 1 }}>{header}</Box>
          {closeButton}
        </Box>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
        }}
      >
        {showFloatingCloseButton ? (
          <Box sx={{ position: 'absolute', right: 24, top: 16, zIndex: 1, '& > button': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)'
          }
         }}>{closeButton}</Box>
        ) : null}
        <Box
          data-slot="codesign-drawer-scroll-area"
          sx={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 2,
            pb: anchor === 'bottom' ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : 2,
            // pr: 2,
            scrollbarGutter: 'stable',
          }}
        >
        {children}
        </Box>
      </Box>
    </>
  )

  if (!overlay) {
    const isSideDrawer = anchor === 'left' || anchor === 'right'
    const persistentSize = isSideDrawer ? sizeStyles.width : sizeStyles.height

    return (
      <Box
        aria-hidden={!open}
        role="presentation"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
          ...(isSideDrawer
            ? {
                height: '100%',
                transition: 'width 180ms ease',
                width: open ? persistentSize : 0,
              }
            : {
                transition: 'height 180ms ease',
                height: open ? persistentSize : 0,
              }),
        }}
      >
        <Box
          data-slot="codesign-drawer-paper"
          sx={{
            backgroundColor: 'background.paper',
            borderColor: 'divider',
            boxShadow: open ? '0 0.5rem 1rem rgba(0,0,0,0.12)' : 'none',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            ...(anchor === 'left' && { borderRight: '1px solid' }),
            ...(anchor === 'right' && { borderLeft: '1px solid' }),
            ...(anchor === 'top' && { borderBottom: '1px solid' }),
            ...(anchor === 'bottom' && {
              borderTop: '1px solid',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }),
            ...(isSideDrawer
              ? {
                  maxWidth: sizeStyles.maxWidth,
                  width: persistentSize,
                }
              : {
                  height: persistentSize,
                  maxHeight: sizeStyles.maxHeight,
                }),
          }}
        >
          {content}
        </Box>
      </Box>
    )
  }

  return (
    <MuiDrawer
      anchor={anchor}
      hideBackdrop={!overlay}
      onClose={onClose}
      open={open}
      variant={overlay ? 'temporary' : 'persistent'}
      slotProps={{
        ...slotProps,
        paper: {
          ...slotProps?.paper,
          sx: {
            display: 'flex',
            ...(anchor === 'bottom' && {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }),
            ...sizeStyles,
            ...(typeof slotProps?.paper === 'object' && slotProps.paper !== null && 'sx' in slotProps.paper ? slotProps.paper.sx : null),
          },
        },
      }}
      {...props}
    >
      {content}
    </MuiDrawer>
  )
}
